import { useState, useEffect } from 'react';
import { Icons } from '../ui/Icons';
import {
  CTAConfig,
  CTAType,
  CTATypeConfig,
  EmailSignupConfig,
  AccountCreationConfig,
  ChurchInviteConfig,
  FreeBookConfig,
  EventRSVPConfig
} from '../../types';

interface CTAEditorModalProps {
  existingCTA?: CTAConfig;
  onSave: (cta: CTAConfig) => void;
  onClose: () => void;
}

const CTA_TYPES: { type: CTAType; label: string; description: string; icon: any }[] = [
  { type: 'email_signup', label: 'Email Signup', description: 'Collect email addresses', icon: Icons.Mail },
  { type: 'account_creation', label: 'Account Creation', description: 'Prompt users to sign up', icon: Icons.UserPlus },
  { type: 'church_invite', label: 'Church Invite', description: 'Share church info & collect RSVPs', icon: Icons.Church },
  { type: 'free_book', label: 'Free Book Offer', description: 'Collect mailing addresses', icon: Icons.Gift },
  { type: 'event_rsvp', label: 'Event RSVP', description: 'Event details & attendance', icon: Icons.Calendar },
];

const generateId = () => `cta_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

const getDefaultConfig = (type: CTAType): CTATypeConfig => {
  switch (type) {
    case 'email_signup':
      return { type: 'email_signup', collectName: false };
    case 'account_creation':
      return { type: 'account_creation', benefits: [] };
    case 'church_invite':
      return { type: 'church_invite', churchName: '', collectRsvp: true };
    case 'free_book':
      return { type: 'free_book', bookTitle: '' };
    case 'event_rsvp':
      return { type: 'event_rsvp', eventName: '', eventDate: '' };
  }
};

export const CTAEditorModal = ({ existingCTA, onSave, onClose }: CTAEditorModalProps) => {
  const [step, setStep] = useState<'type' | 'config'>(existingCTA ? 'config' : 'type');
  const [ctaType, setCtaType] = useState<CTAType>(existingCTA?.type || 'email_signup');
  const [title, setTitle] = useState(existingCTA?.title || '');
  const [description, setDescription] = useState(existingCTA?.description || '');
  const [buttonText, setButtonText] = useState(existingCTA?.buttonText || '');
  const [config, setConfig] = useState<CTATypeConfig>(existingCTA?.config || getDefaultConfig('email_signup'));

  const handleTypeSelect = (type: CTAType) => {
    setCtaType(type);
    setConfig(getDefaultConfig(type));
    // Set default title based on type
    if (!title) {
      const typeInfo = CTA_TYPES.find(t => t.type === type);
      setTitle(typeInfo?.label || '');
    }
    setStep('config');
  };

  const handleSave = () => {
    if (!title.trim()) {
      alert('Please enter a title');
      return;
    }

    const cta: CTAConfig = {
      id: existingCTA?.id || generateId(),
      type: ctaType,
      title: title.trim(),
      description: description.trim() || undefined,
      buttonText: buttonText.trim() || undefined,
      config
    };

    onSave(cta);
  };

  const updateConfig = <K extends keyof CTATypeConfig>(key: string, value: any) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  };

  const renderConfigFields = () => {
    switch (ctaType) {
      case 'email_signup':
        const emailConfig = config as EmailSignupConfig;
        return (
          <div className="space-y-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={emailConfig.collectName || false}
                onChange={(e) => updateConfig('collectName', e.target.checked)}
                className="w-4 h-4 text-primary rounded"
              />
              <span className="text-sm text-gray-700">Also collect name</span>
            </label>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Success Message (optional)
              </label>
              <input
                type="text"
                value={emailConfig.successMessage || ''}
                onChange={(e) => updateConfig('successMessage', e.target.value)}
                placeholder="Thank you for signing up!"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
          </div>
        );

      case 'account_creation':
        const accountConfig = config as AccountCreationConfig;
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Benefits (one per line)
              </label>
              <textarea
                value={(accountConfig.benefits || []).join('\n')}
                onChange={(e) => updateConfig('benefits', e.target.value.split('\n').filter(b => b.trim()))}
                placeholder="Save your progress&#10;Earn achievements&#10;Track your journey"
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
          </div>
        );

      case 'church_invite':
        const churchConfig = config as ChurchInviteConfig;
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Church Name *
              </label>
              <input
                type="text"
                value={churchConfig.churchName || ''}
                onChange={(e) => updateConfig('churchName', e.target.value)}
                placeholder="First Baptist Church"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Address
              </label>
              <input
                type="text"
                value={churchConfig.address || ''}
                onChange={(e) => updateConfig('address', e.target.value)}
                placeholder="123 Main St, City, State"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Service Time
                </label>
                <input
                  type="text"
                  value={churchConfig.serviceTime || ''}
                  onChange={(e) => updateConfig('serviceTime', e.target.value)}
                  placeholder="Sundays at 10am"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contact Email
                </label>
                <input
                  type="email"
                  value={churchConfig.contactEmail || ''}
                  onChange={(e) => updateConfig('contactEmail', e.target.value)}
                  placeholder="info@church.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Map Link (Google Maps URL)
              </label>
              <input
                type="url"
                value={churchConfig.mapLink || ''}
                onChange={(e) => updateConfig('mapLink', e.target.value)}
                placeholder="https://maps.google.com/..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={churchConfig.collectRsvp || false}
                onChange={(e) => updateConfig('collectRsvp', e.target.checked)}
                className="w-4 h-4 text-primary rounded"
              />
              <span className="text-sm text-gray-700">Collect RSVPs from visitors</span>
            </label>
          </div>
        );

      case 'free_book':
        const bookConfig = config as FreeBookConfig;
        const handleBookImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
          const file = e.target.files?.[0];
          if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
              updateConfig('bookImage', reader.result as string);
            };
            reader.readAsDataURL(file);
          }
        };
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Book Title *
              </label>
              <input
                type="text"
                value={bookConfig.bookTitle || ''}
                onChange={(e) => updateConfig('bookTitle', e.target.value)}
                placeholder="Journey Through Faith"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Book Description
              </label>
              <textarea
                value={bookConfig.bookDescription || ''}
                onChange={(e) => updateConfig('bookDescription', e.target.value)}
                placeholder="A guide to deepening your faith..."
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Book Cover Image (optional)
              </label>
              <div className="flex items-start gap-4">
                {bookConfig.bookImage ? (
                  <div className="relative">
                    <img
                      src={bookConfig.bookImage}
                      alt="Book cover"
                      className="w-20 h-28 object-cover rounded shadow-md"
                    />
                    <button
                      type="button"
                      onClick={() => updateConfig('bookImage', undefined)}
                      className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                    >
                      <Icons.X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="w-20 h-28 bg-gray-100 border-2 border-dashed border-gray-300 rounded flex items-center justify-center">
                    <Icons.Image className="w-6 h-6 text-gray-400" />
                  </div>
                )}
                <div className="flex-1">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleBookImageUpload}
                    className="block w-full text-sm text-gray-500 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-amber-50 file:text-amber-700 hover:file:bg-amber-100"
                  />
                  <p className="mt-1 text-xs text-gray-500">Upload a cover image for the book</p>
                </div>
              </div>
            </div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={bookConfig.collectPhone || false}
                onChange={(e) => updateConfig('collectPhone', e.target.checked)}
                className="w-4 h-4 text-primary rounded"
              />
              <span className="text-sm text-gray-700">Also collect phone number</span>
            </label>
          </div>
        );

      case 'event_rsvp':
        const eventConfig = config as EventRSVPConfig;
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Event Name *
              </label>
              <input
                type="text"
                value={eventConfig.eventName || ''}
                onChange={(e) => updateConfig('eventName', e.target.value)}
                placeholder="Summer Bible Camp"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Event Date *
                </label>
                <input
                  type="date"
                  value={eventConfig.eventDate || ''}
                  onChange={(e) => updateConfig('eventDate', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Event Time
                </label>
                <input
                  type="text"
                  value={eventConfig.eventTime || ''}
                  onChange={(e) => updateConfig('eventTime', e.target.value)}
                  placeholder="6:00 PM"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Location
              </label>
              <input
                type="text"
                value={eventConfig.eventLocation || ''}
                onChange={(e) => updateConfig('eventLocation', e.target.value)}
                placeholder="Church Fellowship Hall"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Event Description
              </label>
              <textarea
                value={eventConfig.eventDescription || ''}
                onChange={(e) => updateConfig('eventDescription', e.target.value)}
                placeholder="Join us for a fun-filled week..."
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div className="flex gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={eventConfig.collectPhone || false}
                  onChange={(e) => updateConfig('collectPhone', e.target.checked)}
                  className="w-4 h-4 text-primary rounded"
                />
                <span className="text-sm text-gray-700">Collect phone</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={eventConfig.collectGuestCount || false}
                  onChange={(e) => updateConfig('collectGuestCount', e.target.checked)}
                  className="w-4 h-4 text-primary rounded"
                />
                <span className="text-sm text-gray-700">Collect guest count</span>
              </label>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            {step === 'config' && (
              <button
                onClick={() => setStep('type')}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <Icons.Prev className="w-5 h-5" />
              </button>
            )}
            <h2 className="text-lg font-bold">
              {existingCTA ? 'Edit Call To Action' : step === 'type' ? 'Add Call To Action' : 'Configure CTA'}
            </h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <Icons.X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[calc(90vh-140px)]">
          {step === 'type' ? (
            <div className="space-y-3">
              <p className="text-sm text-gray-600 mb-4">
                Choose the type of action you want users to take:
              </p>
              {CTA_TYPES.map(({ type, label, description, icon: Icon }) => (
                <button
                  key={type}
                  onClick={() => handleTypeSelect(type)}
                  className="w-full p-4 border-2 border-gray-200 rounded-xl hover:border-primary hover:bg-blue-50 transition-all text-left flex items-center gap-4"
                >
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{label}</h3>
                    <p className="text-sm text-gray-600">{description}</p>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="space-y-6">
              {/* Common Fields */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Title *
                  </label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Join Our Newsletter"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Get weekly devotionals and updates..."
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Button Text
                  </label>
                  <input
                    type="text"
                    value={buttonText}
                    onChange={(e) => setButtonText(e.target.value)}
                    placeholder="Sign Up"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>

              {/* Type-specific Config */}
              <div className="border-t pt-4">
                <h3 className="text-sm font-medium text-gray-700 mb-3">
                  {CTA_TYPES.find(t => t.type === ctaType)?.label} Settings
                </h3>
                {renderConfigFields()}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {step === 'config' && (
          <div className="flex justify-end gap-3 p-4 border-t">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-blue-700"
            >
              {existingCTA ? 'Save Changes' : 'Add CTA'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
